/* ============================================
   SETTINGS → PORTAL USERS: STARTING MODULES, EXTERNAL USERS, THE PARKED MEMO
   assets/rep-settings-portal-access.js  (markup: snippets/rep-settings-portal-access.liquid)
   CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-09-26)
   ============================================
   ⛔ EVERY NEW USER STARTS WITH EVERYTHING OFF. The invite forms send the drawer's whole module
   catalog plus the ticked boxes; rfq-worker writes the new person's own record with every other
   module hidden. Without the catalog the worker refuses the invite (never falls back to the team
   default), so a page that failed to load this file fails LOUDLY.
   ⛔ AN EXTERNAL USER (role 'agency': an agency member or a freelancer) never inherits the team
   default and may hold only EXTERNAL_MODULES — the modules whose worker can fence them. The list
   is rfq-worker lib/external-access.js; this copy only decides what the screen OFFERS, the worker
   refuses anything else by name. External codes start AGY- (the worker mints them that way).
   Exposed as window.RepPortalAccess for the section's inline handlers:
     invitePayload('team' | 'external') → { modules: {catalog, granted, labels}, orgKind? }
     kind() → 'agency' | 'freelancer'
     onTarget(code, pageConfigCache)    → Module Visibility, after a target is chosen
     decorateTargets(selectEl)          → marks external users in the "Configure for" list
   No dynamic import(): the Shopify CDN minifier rewrites it into require(), which hangs.

   CUSTOM_SOCIALBOT_BY_CLAUDE (Social planner 1.9, Ryan 2026-09-26) — "Social · Contribute".
   A second external key, `socialcontrib`, that is NOT a drawer module: it lets a freelancer edit
   and draft on Social, and only counts together with Social itself (social-worker canContribute).
   It is drawn as a child row under Social for an external target only, and offered as a box on
   the external invite form. Off by default, like every module. The Save loop in
   rep-quote-settings.liquid reads every .rep-settings__toggle-row, so no Liquid change.

   CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-22) — "Content Library · Create ads" (`contentlibraryads`): who
   sees the Create ads tab. A child row under Content Library for EVERY target (team default, a rep, an external user),
   because it is per person for the team too (`team: true`). Team: a missing key reads Enabled (team on); external: a
   missing key reads Hidden (outside users off). rfq-worker enforces both (contentLibraryAdsAllowed + MODULE_PATHS).
   ============================================ */
(function () {
  'use strict';
  var EXTERNAL_MODULES = ['marketingops', 'adsreview', 'blogbot', 'socialbot', 'contentlibrary', 'socialcontrib', 'gymlayout', 'contentlibraryads', 'weightdesigner'];
  /* Grants that are not drawer modules, placed right after the module they refine. `team`: also a row for team targets. */
  var CHILD_GRANTS = [
    { key: 'socialcontrib', after: 'socialbot', label: 'Social \u00b7 Contribute', note: 'edit, draft &amp; upload; only with Social on' },
    { key: 'contentlibraryads', after: 'contentlibrary', label: 'Content Library \u00b7 Create ads', note: 'the Create ads tab; only with Content Library on', team: true },
  ];
  var MW_BASE = 'https://marketing-worker.ryan-29b.workers.dev';

  function isExternalCode(code) { return /^AGY-/i.test(String(code || '')); }

  function drawerModules() {
    var D = window.RepPortal && window.RepPortal.Drawer;
    var cats = (D && D.MODULES) || [];
    var out = [];
    for (var i = 0; i < cats.length; i++) {
      var items = cats[i].items || [];
      for (var j = 0; j < items.length; j++) {
        if (items[j].page) out.push({ key: items[j].page, label: items[j].label, adminOnly: !!items[j].adminOnly });
      }
    }
    return out;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* The boxes a form offers: a team invite gets every module an admin could give a rep (the
     admin-only ones are locked for non-admins whatever is ticked, so they are not offered); an
     external invite gets only the modules an external user can hold. */
  function offered(form) {
    var list = drawerModules().filter(function (m) {
      return form === 'external' ? EXTERNAL_MODULES.indexOf(m.key) !== -1 : !m.adminOnly;
    });
    if (form !== 'external') return list;
    /* A child grant sits right after its parent, so "Social · Contribute" reads as part of Social. */
    CHILD_GRANTS.forEach(function (c) {
      var at = -1;
      for (var i = 0; i < list.length; i++) if (list[i].key === c.after) at = i;
      if (at !== -1) list.splice(at + 1, 0, { key: c.key, label: c.label, adminOnly: false });
    });
    return list;
  }

  function renderBoxes() {
    var boxes = document.querySelectorAll('[data-pa-modules]');
    if (!boxes.length) return true;
    if (!drawerModules().length) return false;
    for (var b = 0; b < boxes.length; b++) {
      var form = boxes[b].getAttribute('data-pa-modules');
      boxes[b].innerHTML = offered(form).map(function (m) {
        return '<label style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;">' +
          '<input type="checkbox" data-pa-module="' + esc(m.key) + '"> ' + esc(m.label) + '</label>';
      }).join('');
    }
    return true;
  }

  function kind() {
    var sel = document.getElementById('pa-invite-kind');
    return sel && sel.value === 'freelancer' ? 'freelancer' : 'agency';
  }

  /* A freelancer IS their own org: the slug and name come from their own name on the worker, so
     the two agency fields are hidden rather than left there to be filled in wrongly. */
  function applyKind() {
    var free = kind() === 'freelancer';
    ['agency-invite-id', 'agency-invite-display'].forEach(function (id) {
      var el = document.getElementById(id);
      var field = el && el.closest('.rep-settings__field');
      if (field) field.style.display = free ? 'none' : '';
    });
  }

  function invitePayload(form) {
    var box = document.querySelector('[data-pa-modules="' + form + '"]');
    var catalog = drawerModules().map(function (m) { return m.key; });
    var labels = {};
    drawerModules().forEach(function (m) { labels[m.key] = m.label; });
    /* The worker refuses a ticked key that is not in the catalog, so an external invite's catalog
       carries the child grants too. */
    if (form === 'external') CHILD_GRANTS.forEach(function (c) { catalog.push(c.key); labels[c.key] = c.label; });
    var granted = [];
    if (box) {
      var ticks = box.querySelectorAll('input[data-pa-module]');
      for (var i = 0; i < ticks.length; i++) if (ticks[i].checked) granted.push(ticks[i].getAttribute('data-pa-module'));
    }
    var out = { modules: { catalog: catalog, granted: granted, labels: labels } };
    if (form === 'external') out.orgKind = kind();
    return out;
  }

  function clearTicks(form) {
    var box = document.querySelector('[data-pa-modules="' + form + '"]');
    if (!box) return;
    var ticks = box.querySelectorAll('input[data-pa-module]');
    for (var i = 0; i < ticks.length; i++) ticks[i].checked = false;
  }

  /* Module Visibility: an external target is shown ITS OWN record only (never the team default),
     and a module it cannot hold is locked at Hidden with a "Team only" tag, so a Save can never
     send a grant the worker would refuse. A team target gets every row back, unchanged. */
  /* The child-grant rows are rebuilt on every target change. An external target gets every child grant; a team target
     only the ones marked `team` (so it never carries, or saves, a key it cannot hold). Off by default for an external
     target; for a team target a `team` grant is on unless it was switched off. */
  function syncChildRows(ext, own) {
    var old = document.querySelectorAll('#settings-page-toggles [data-pa-child]');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    CHILD_GRANTS.forEach(function (c) {
      if (!ext && !c.team) return;
      var parent = document.querySelector('#settings-page-toggles .rep-settings__toggle-row[data-page="' + c.after + '"]');
      if (!parent) return;
      var row = document.createElement('div');
      row.className = 'rep-settings__toggle-row';
      row.setAttribute('data-page', c.key);
      row.setAttribute('data-pa-child', '1');
      var v = own[c.key];
      var on = ext ? (v === true || v === 'enabled') : !(v === false || v === 'hidden' || v === 'disabled');
      row.innerHTML = '<span class="rep-settings__toggle-label" style="padding-left:18px;">\u21b3 ' + esc(c.label)
        + ' <span style="font-size:12px;color:var(--rq-text-muted);">' + (c.note || '') + '</span></span>'
        + '<select class="rep-settings__page-status">'
        + '<option value="enabled"' + (on ? ' selected' : '') + '>Enabled</option>'
        + '<option value="hidden"' + (on ? '' : ' selected') + '>Hidden</option>'
        + '</select>';
      parent.parentNode.insertBefore(row, parent.nextSibling);
    });
  }

  function onTarget(code, cache) {
    var ext = isExternalCode(code);
    var own = (cache && cache[code] && cache[code].pages) || {};
    /* a team member with no record of their own is shown the team default (what a Save would copy) */
    var shown = ext ? own : ((cache && cache[code] && cache[code].pages) || (cache && cache.__global && cache.__global.pages) || {});
    syncChildRows(ext, shown);
    var rows = document.querySelectorAll('#settings-page-toggles .rep-settings__toggle-row');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var key = row.getAttribute('data-page');
      var sel = row.querySelector('.rep-settings__page-status');
      var tag = row.querySelector('.pa-team-only');
      if (!sel) continue;
      if (!ext) { sel.disabled = false; if (tag) tag.parentNode.removeChild(tag); continue; }
      if (EXTERNAL_MODULES.indexOf(key) === -1) {
        sel.value = 'hidden';
        sel.disabled = true;
        if (!tag) {
          tag = document.createElement('span');
          tag.className = 'pa-team-only';
          tag.style.cssText = 'font-size:12px;color:var(--rq-text-muted);margin-right:8px;';
          tag.textContent = 'Team only';
          row.insertBefore(tag, sel);
        }
      } else {
        var v = own[key];
        sel.value = (v === true || v === 'enabled') ? 'enabled' : 'hidden';
        sel.disabled = false;
        if (tag) tag.parentNode.removeChild(tag);
      }
    }
    var note = document.getElementById('settings-page-target-note');
    if (note && ext) {
      note.innerHTML = '<strong>' + esc(code) + '</strong> is an external user. They see only what is switched on here; ' +
        'everything else stays off, including modules added later. <em>Team only</em> modules cannot be given to someone outside the company. ' +
        'A change reaches their open session within 30 minutes.';
    }
  }

  function decorateTargets(select) {
    if (!select) return;
    for (var i = 0; i < select.options.length; i++) {
      var o = select.options[i];
      if (isExternalCode(o.value) && o.textContent.indexOf('External') === -1) o.textContent += ' · External';
    }
  }

  /* ---- the parked Monday Memo: one switch, absent = off, a person flips it ---- */
  async function mw(path, init) {
    var token = await window.RepPortal.Auth.getAccessToken();
    var resp = await fetch(MW_BASE + path, Object.assign({
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    }, init || {}));
    var body = {};
    try { body = await resp.json(); } catch (e) {}
    if (!resp.ok) throw new Error(body.error || ('Marketing worker ' + resp.status));
    return body;
  }

  function paintMemo(enabled) {
    var state = document.getElementById('pa-memo-state');
    var btn = document.getElementById('pa-memo-toggle');
    if (state) state.innerHTML = enabled ? 'It is <strong>ON</strong>: it sends every Sunday.' : 'It is <strong>OFF</strong>: nothing sends.';
    if (btn) {
      btn.style.display = '';
      btn.textContent = enabled ? 'Turn the Monday Memo off' : 'Turn the Monday Memo on';
      btn.setAttribute('data-enabled', enabled ? '1' : '0');
    }
  }

  function wireMemo() {
    var box = document.getElementById('pa-memo');
    if (!box || box.getAttribute('data-wired')) return;
    box.setAttribute('data-wired', '1');
    mw('/admin/monday-memo-config').then(function (d) { paintMemo(!!d.enabled); }).catch(function (e) {
      var state = document.getElementById('pa-memo-state');
      if (state) state.textContent = 'Could not read the switch (' + e.message + ').';
    });
    var btn = document.getElementById('pa-memo-toggle');
    if (btn) btn.addEventListener('click', function () {
      var turnOn = btn.getAttribute('data-enabled') !== '1';
      if (turnOn && !window.confirm('Turn the Monday Memo on? It emails its recipient list every Sunday evening.')) return;
      var msg = document.getElementById('pa-memo-msg');
      btn.disabled = true;
      mw('/admin/monday-memo-config', { method: 'POST', body: JSON.stringify({ enabled: turnOn }) })
        .then(function (d) { paintMemo(!!d.enabled); if (msg) msg.textContent = ''; })
        .catch(function (e) { if (msg) msg.textContent = e.message; })
        .then(function () { btn.disabled = false; });
    });
    var show = document.getElementById('pa-memo-show');
    if (show) show.addEventListener('click', function () {
      var tab = document.getElementById('account-tab-memo');
      if (tab) { tab.style.display = ''; tab.click(); tab.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    });
  }

  window.RepPortalAccess = {
    EXTERNAL_MODULES: EXTERNAL_MODULES, CHILD_GRANTS: CHILD_GRANTS,
    invitePayload: invitePayload, clearTicks: clearTicks, kind: kind,
    onTarget: onTarget, decorateTargets: decorateTargets, isExternalCode: isExternalCode,
  };

  function boot() {
    var sel = document.getElementById('pa-invite-kind');
    if (sel && !sel.getAttribute('data-wired')) {
      sel.setAttribute('data-wired', '1');
      sel.addEventListener('change', applyKind);
      applyKind();
    }
    var s = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession();
    if (s && s.admin) wireMemo();
    var tries = 0;
    (function draw() { if (!renderBoxes() && ++tries < 40) setTimeout(draw, 250); })();
  }
  window.addEventListener('rep-portal:authenticated', boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
